import { Stack, Text, Timeline } from '@mantine/core';
import { IconCheck, IconX } from '@tabler/icons-react';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  TravelDocumentKinds,
  TravelRequest,
  TravelRequestStatus,
  travelBoardReached,
  travelSubmissionDocumentsComplete,
} from '../model/TravelRequestTypes';

export const travelProcessStepKeyList = [
  'fill',
  'attach',
  'submit',
  'approval',
  'administrativeBoard',
  'travelOrder',
  'returnDocuments',
  'complete',
] as const;

export type TravelProcessStepKey = (typeof travelProcessStepKeyList)[number];

interface TravelProcessTimelineProps {
  status: TravelRequestStatus;
  submissionDocumentsComplete: boolean;
  returnDocumentsComplete?: boolean;
  showBoardStep?: boolean;
  boardReached?: boolean;
  content?: Partial<Record<TravelProcessStepKey, ReactNode>>;
}

// A saved draft has its form behind it, so the view opens on the documents step.
const keyByStatus: Record<TravelRequestStatus, TravelProcessStepKey | 'done'> = {
  DRAFT: 'attach',
  RETURNED_FOR_CHANGES: 'fill',
  SUBMITTED: 'approval',
  REJECTED: 'approval',
  TRAVEL_ORDER_ISSUED: 'travelOrder',
  APPROVED: 'returnDocuments',
  COMPLETED: 'done',
  CANCELLED: 'returnDocuments',
};

const stoppedStatuses: TravelRequestStatus[] = ['REJECTED', 'CANCELLED'];

function activeKey(
  status: TravelRequestStatus,
  submissionDocumentsComplete: boolean,
  returnDocumentsComplete: boolean,
  boardReached: boolean,
): TravelProcessStepKey | 'done' {
  if (status === 'DRAFT' && submissionDocumentsComplete) {
    return 'submit';
  }
  if (status === 'APPROVED' && returnDocumentsComplete) {
    return 'complete';
  }
  const key = keyByStatus[status];
  return key === 'approval' && boardReached ? 'administrativeBoard' : key;
}

export function travelProcessNextKey(
  status: TravelRequestStatus,
  submissionDocumentsComplete: boolean,
  returnDocumentsComplete: boolean,
  boardReached = false,
) {
  if (stoppedStatuses.includes(status)) {
    return 'stopped';
  }
  return activeKey(status, submissionDocumentsComplete, returnDocumentsComplete, boardReached);
}

export function travelProcessStepId(key: TravelProcessStepKey) {
  return `travel-step-${key}`;
}

export function travelProcessNextStepId(
  request: TravelRequest,
  submissionDocumentsComplete: boolean,
  returnDocumentsComplete: boolean,
) {
  const key = travelProcessNextKey(
    request.status,
    submissionDocumentsComplete,
    returnDocumentsComplete,
    travelBoardReached(request),
  );
  return key === 'stopped' || key === 'done' ? undefined : travelProcessStepId(key);
}

// Kinds that failed to load arrive as undefined and keep the new draft on the documents step.
export function travelCreatedLandingStepId(created: TravelRequest, documentKinds: TravelDocumentKinds | undefined) {
  return travelProcessNextStepId(created, travelSubmissionDocumentsComplete(created, documentKinds), false);
}

function stepState(index: number, active: number) {
  if (index < active) {
    return 'done';
  }
  return index === active ? 'active' : 'future';
}

function stepBullet(failed: boolean, done: boolean, position: number) {
  if (failed) {
    return <IconX size={14} />;
  }
  return done ? <IconCheck size={14} /> : position;
}

export function TravelProcessTimeline({
  status,
  submissionDocumentsComplete,
  returnDocumentsComplete = false,
  showBoardStep = false,
  boardReached = false,
  content,
}: TravelProcessTimelineProps) {
  const { t } = useTranslation();
  const stopped = stoppedStatuses.includes(status);
  const steps = travelProcessStepKeyList.filter(key => showBoardStep || key !== 'administrativeBoard');
  const current = activeKey(
    status,
    submissionDocumentsComplete,
    returnDocumentsComplete,
    showBoardStep && boardReached,
  );
  const active = current === 'done' ? steps.length : steps.indexOf(current);

  return (
    <Timeline
      active={active}
      bulletSize={24}
      lineWidth={2}
    >
      {steps.map((key, index) => {
        const state = stepState(index, active);
        const failed = stopped && key === current;

        return (
          <Timeline.Item
            key={key}
            id={travelProcessStepId(key)}
            style={{ scrollMarginTop: 'calc(var(--app-shell-header-height, 0px) + var(--mantine-spacing-md))' }}
            role="group"
            aria-label={t(`travel.requests.process.${key}`)}
            aria-current={state === 'active' ? 'step' : undefined}
            mod={{ state }}
            color={failed ? 'red' : undefined}
            bullet={stepBullet(failed, state === 'done', index + 1)}
            title={
              <Text
                size="sm"
                fw={500}
                c={state === 'future' ? 'dimmed' : undefined}
              >
                {t(`travel.requests.process.${key}`)}
              </Text>
            }
          >
            <Stack gap="xs">
              <Text
                size="xs"
                c="dimmed"
              >
                {t(`travel.requests.process.description.${key}`)}
              </Text>
              {content?.[key]}
            </Stack>
          </Timeline.Item>
        );
      })}
    </Timeline>
  );
}
